/**
 * FormRenderer — the single shared form component.
 *
 * Used by three surfaces with the same renderer; each surface configures it
 * via `mode`:
 *   - 'data-entry' — end users fill forms (records workspace + dedicated page)
 *   - 'review'     — reviewers view existing content and add fields
 *   - 'preview'    — preview pane inside the form builder
 *
 * Internal pipeline:
 *   1. Run the engine (condition + calculated + validation) on every render.
 *   2. Gate blocking errors by `touched` / `submitAttempted` (Step 4).
 *   3. Render the banner row, then one `FieldRenderer` per visible field.
 *
 * The component is purely presentational. Data fetching, save mutations, and
 * page chrome live in the surface-specific container.
 *
 * Submit-time validation: the container calls `runFormEngine` separately to
 * decide whether the save mutation should fire. To make the user see *all*
 * outstanding errors at that moment, the container sets `submitAttempted` to
 * true — that flips the gating off and every visible blocking error renders
 * inline.
 */
import { useEffect, useMemo, useState, useCallback } from 'react'
import type React from 'react'
import { AlertCircle, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import type {
  FormDefinition,
  FieldDefinition,
} from '@/features/form-builder/api/form-builder-api'
import {
  runFormEngine,
  gateErrorsByTouched,
  groupWarningsByField,
  type EngineSnapshot,
} from '@bluedatalake/form-engine'
import { FieldRenderer } from './field-renderer'
import type { FileUploadContext } from './input-props'

export type { FileUploadContext }

export type FormRendererMode = 'data-entry' | 'review' | 'preview'

export interface FormRendererProps {
  definition: FormDefinition
  values: Record<string, unknown>
  onChange: (key: string, value: unknown) => void

  mode: FormRendererMode

  /** Master read-only switch (e.g., submitted form viewed by Data Entry). */
  readOnly?: boolean

  /** Justifications keyed by `${fieldKey}_${ruleId}`. */
  justifications?: Record<string, string>
  onJustificationChange?: (jKey: string, val: string) => void

  /** Whether the user has clicked Submit at least once (Step 4). */
  submitAttempted?: boolean
  /** Reset touched state — useful when the form definition / record swaps. */
  resetSignal?: unknown

  /** Required for `file` / `image` fields to actually upload. */
  uploadCtx?: FileUploadContext

  /** Field keys that should display a "needs revision" marker (Phase 9.3). */
  revisionFieldKeys?: string[]

  /**
   * Render the field-level comments thread for a given field. Optional —
   * preview mode never uses this.
   */
  renderFieldComments?: (fieldKey: string) => React.ReactNode
  /** Field keys that have at least one unresolved decline comment. */
  declineCommentFieldKeys?: string[]
  /**
   * Render prior-justification history for a given (fieldKey, ruleId). Used by
   * approver / read-only views to surface `soft_warning_flags[*].history[]`
   * inline under each warned field. Return null when there's nothing to show.
   */
  renderJustificationHistory?: (fieldKey: string, ruleId: string) => React.ReactNode

  /** Optional className applied to the wrapper. */
  className?: string

  /**
   * Lets the parent observe the engine snapshot on every render — useful for
   * disabling the submit button or showing top-level banners. The snapshot is
   * cheap to recompute, so callers usually just call `runFormEngine` directly
   * inside their own handlers.
   */
  onSnapshotChange?: (snapshot: EngineSnapshot) => void
}

export function FormRenderer({
  definition, values, onChange, mode,
  readOnly,
  justifications = {}, onJustificationChange,
  submitAttempted,
  resetSignal,
  uploadCtx,
  revisionFieldKeys,
  renderFieldComments, declineCommentFieldKeys,
  renderJustificationHistory,
  className,
  onSnapshotChange,
}: FormRendererProps) {
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  // Reset touched state when the form/record changes (panel reuse).
  useEffect(() => {
    setTouched({})
  }, [resetSignal])

  const snapshot = useMemo(
    () => runFormEngine(definition.fields, definition.conditional_rules ?? [], values),
    [definition.fields, definition.conditional_rules, values],
  )

  useEffect(() => {
    onSnapshotChange?.(snapshot)
  }, [snapshot, onSnapshotChange])

  const gatedErrors = useMemo(
    () => gateErrorsByTouched(snapshot.blockingErrors, touched, !!submitAttempted),
    [snapshot.blockingErrors, touched, submitAttempted],
  )

  const warningsByField = useMemo(
    () => groupWarningsByField(snapshot.softWarnings),
    [snapshot.softWarnings],
  )

  const markTouched = useCallback((key: string) => {
    setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }))
  }, [])

  const fields = definition.fields
  const visibleFields = fields.filter((f) => snapshot.renderStates[f.field_key]?.visible !== false)
  const hiddenCount = fields.length - visibleFields.length
  const blockingCount = Object.keys(gatedErrors).length
  const missingJustifications = snapshot.softWarnings.filter(
    (w) => !(justifications[`${w.fieldKey}_${w.ruleId}`] ?? '').trim(),
  )

  return (
    <div className={cn('space-y-5', className)}>
      {/* Top inline summary banners. The preview pane wraps this in its own
          chrome already; the data-entry container also wraps with extra UI
          for save/submit. Both can hide these banners by passing
          `mode='preview'` or rendering their own. */}
      {mode !== 'preview' && blockingCount > 0 && (
        <Banner tone="destructive">
          <AlertCircle className="w-4 h-4 shrink-0" />
          لطفاً فیلدهای اجباری را تکمیل کنید
        </Banner>
      )}
      {mode !== 'preview' && missingJustifications.length > 0 && (
        <Banner tone="warning">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          {missingJustifications.length.toLocaleString('fa-IR')} هشدار اعتبارسنجی نیاز به توجیه بالینی دارد — زیر هر فیلد توضیح دهید
        </Banner>
      )}

      {fields.length === 0 && (
        <div className="text-center py-16 text-muted-foreground text-sm">
          این فرم هنوز فیلدی ندارد
        </div>
      )}

      {fields.map((field) => {
        const state = snapshot.renderStates[field.field_key]
        if (state && !state.visible) return null

        const fkey = field.field_key
        const fieldDisabled = readOnly || (state ? !state.enabled : false)
        const errorForField = gatedErrors[fkey]?.message
        const valueForField =
          field.field_type === 'calculated'
            ? snapshot.computedValues[fkey]
            : values[fkey]

        const handleChange = (val: unknown) => {
          if (readOnly) return
          onChange(fkey, val)
          markTouched(fkey)
        }

        return (
          <FieldRenderer
            key={fkey}
            field={field}
            value={valueForField}
            onChange={handleChange}
            onBlur={() => markTouched(fkey)}
            required={state?.required ?? field.is_required}
            enabled={state?.enabled ?? true}
            filteredOptions={state?.filteredOptions}
            disabled={fieldDisabled}
            error={errorForField}
            warnings={warningsByField[fkey] ?? []}
            justifications={justifications}
            onJustificationChange={(jKey, val) => onJustificationChange?.(jKey, val)}
            uploadCtx={uploadCtx}
            needsRevision={revisionFieldKeys?.includes(fkey)}
            hasDeclineComments={declineCommentFieldKeys?.includes(fkey)}
            commentSlot={renderFieldComments?.(fkey)}
            renderJustificationHistory={renderJustificationHistory}
          />
        )
      })}

      {hiddenCount > 0 && (
        <p className="text-xs text-muted-foreground text-center">
          {hiddenCount.toLocaleString('fa-IR')} فیلد بر اساس پاسخ‌های شما مخفی شده
        </p>
      )}
    </div>
  )
}

function Banner({
  tone, children,
}: {
  tone: 'destructive' | 'warning' | 'info'
  children: React.ReactNode
}) {
  const toneCls =
    tone === 'destructive'
      ? 'bg-destructive/10 border-destructive/30 text-red-400'
      : tone === 'warning'
      ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
      : 'bg-blue-500/10 border-blue-500/20 text-blue-400'
  return (
    <div className={cn('flex items-center gap-2 rounded-lg border px-4 py-2 text-xs', toneCls)}>
      {children}
    </div>
  )
}

// Re-export the FieldDefinition type for convenience in containers.
export type { FieldDefinition }
